import { useState } from 'react';
import { ChatCircle, X, PaperPlaneRight, WhatsappLogo, TelegramLogo } from '@phosphor-icons/react';
import { trackEvent, GA_EVENTS } from '../utils/analytics';

const WHATSAPP_NUMBER = '19383071829';
const TELEGRAM_LINK = 'https://t.me/CADPartner';

export function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [showChannels, setShowChannels] = useState(false);
  const [messages, setMessages] = useState([
    { type: 'bot', text: 'Hello! Welcome to SurveysMaps. How can I help you today?' }
  ]);
  const [input, setInput] = useState('');

  const handleSend = () => {
    if (!input.trim()) return;
    setMessages(prev => [...prev, { type: 'user', text: input }]);
    setInput('');
    setTimeout(() => {
      setMessages(prev => [...prev, { 
        type: 'bot', 
        text: 'Thank you for your message! Our team will get back to you shortly. For faster response, reach us on WhatsApp or Telegram!' 
      }]);
    }, 1000);
  };

  const toggleMenu = () => {
    if (isOpen) {
      setIsOpen(false);
      setShowChannels(false);
    } else {
      setShowChannels(!showChannels);
    }
  };

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3" data-testid="chat-widget">
      {/* Chat Window */}
      {isOpen && (
        <div className="w-80 bg-white border border-zinc-200 shadow-xl rounded-lg overflow-hidden animate-fade-in mb-3" data-testid="chat-window">
          <div className="bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] text-white p-4 flex items-center justify-between">
            <div>
              <h4 className="font-semibold" style={{ fontFamily: 'Outfit' }}>SurveysMaps Chat</h4>
              <p className="text-xs text-white/80">We typically reply within minutes</p>
            </div>
            <button 
              onClick={() => { setIsOpen(false); setShowChannels(false); }}
              className="p-1 hover:bg-white/20 transition-colors rounded"
              data-testid="chat-close-btn"
            >
              <X size={20} />
            </button>
          </div>
          <div className="h-56 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] px-3 py-2 text-sm ${
                  msg.type === 'user' 
                    ? 'bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] text-white rounded-lg' 
                    : 'bg-[#F3F0EB] text-zinc-800 rounded-lg'
                }`}>
                  {msg.text}
                </div>
              </div>
            ))}
          </div>
          {/* Quick channel links */}
          <div className="px-4 py-2 bg-[#F3F0EB] border-t border-zinc-200 flex gap-2">
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}?text=Hi%20SurveysMaps%2C%20I%20need%20help%20with%20a%20CAD%20drafting%20project.`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-1.5 py-1.5 bg-[#25D366] text-white text-xs font-medium rounded hover:bg-[#20bd5a] transition-colors"
              data-testid="chat-whatsapp-quick"
            >
              <WhatsappLogo size={14} weight="fill" /> WhatsApp
            </a>
            <a
              href={TELEGRAM_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-1.5 py-1.5 bg-[#0088cc] text-white text-xs font-medium rounded hover:bg-[#0077b3] transition-colors"
              data-testid="chat-telegram-quick"
            >
              <TelegramLogo size={14} weight="fill" /> Telegram
            </a>
          </div>
          <div className="border-t border-zinc-200 p-3 flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSend()}
              placeholder="Type a message..."
              className="flex-1 px-3 py-2 border border-zinc-200 text-sm rounded focus:outline-none focus:border-[#3B82F6]"
              data-testid="chat-input"
            />
            <button 
              onClick={handleSend}
              className="bg-[#3B82F6] text-white p-2 rounded hover:bg-[#2563EB] transition-colors"
              data-testid="chat-send-btn"
            >
              <PaperPlaneRight size={20} />
            </button>
          </div>
        </div>
      )}

      {/* Floating Channel Buttons */}
      {showChannels && !isOpen && (
        <div className="flex flex-col items-end gap-2 animate-fade-in-up" data-testid="channel-buttons">
          {/* WhatsApp */}
          <a
            href={`https://wa.me/${WHATSAPP_NUMBER}?text=Hi%20SurveysMaps%2C%20I%20need%20help%20with%20a%20CAD%20drafting%20project.`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-[#25D366] text-white pl-4 pr-3 py-2.5 rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all"
            data-testid="whatsapp-btn"
            onClick={() => trackEvent(GA_EVENTS.WHATSAPP_CLICK, { source: 'floating_menu' })}
          >
            <span className="text-sm font-medium">WhatsApp</span>
            <WhatsappLogo size={22} weight="fill" />
          </a>
          {/* Telegram */}
          <a
            href={TELEGRAM_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-[#0088cc] text-white pl-4 pr-3 py-2.5 rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all"
            data-testid="telegram-btn"
            onClick={() => trackEvent(GA_EVENTS.TELEGRAM_CLICK, { source: 'floating_menu' })}
          >
            <span className="text-sm font-medium">Telegram</span>
            <TelegramLogo size={22} weight="fill" />
          </a>
          {/* Live Chat */}
          <button
            onClick={() => { setIsOpen(true); setShowChannels(false); }}
            className="flex items-center gap-2 bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] text-white pl-4 pr-3 py-2.5 rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all"
            data-testid="livechat-btn"
          >
            <span className="text-sm font-medium">Live Chat</span>
            <ChatCircle size={22} weight="fill" />
          </button>
        </div>
      )}

      {/* Main Floating Action Button */}
      <button 
        onClick={toggleMenu}
        className="w-14 h-14 flex items-center justify-center rounded-full shadow-lg hover:scale-110 transition-all"
        style={{ background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' }}
        data-testid="chat-bubble"
      >
        {(isOpen || showChannels) 
          ? <X size={24} weight="bold" className="text-white" /> 
          : <ChatCircle size={24} weight="bold" className="text-white" />
        }
      </button>
    </div>
  );
}
